/**
 * portalAuditLog.js — Portal-side audit event writer
 *
 * Web portal equivalent of the POS app's utils/auditLog.js.
 * Writes to: restaurants_data/{restaurantId}/audit_log
 *
 * Usage:
 *   import { writePortalAuditEvent, PORTAL_AUDIT } from '../../utilities/portalAuditLog';
 *   writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.GENERAL_SETTINGS_SAVED, { field: 'restaurantName' });
 *
 * user shape (Firebase Auth object from portal session):
 *   { uid, email, displayName }
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// ── Action constants ─────────────────────────────────────────────────────────

export const PORTAL_AUDIT = {
  // Settings changes
  GENERAL_SETTINGS_SAVED:       'general_settings_saved',
  PICKUP_SETTINGS_SAVED:        'pickup_settings_saved',
  MESSAGING_SETTINGS_SAVED:     'messaging_settings_saved',
  STRIPE_CONNECTED:             'stripe_connected',
  COURSES_SAVED:                'courses_saved',
  ENFORCEMENT_SAVED:            'enforcement_saved',

  // Employee management
  EMPLOYEE_CREATED:             'employee_created',
  EMPLOYEE_UPDATED:             'employee_updated',
  EMPLOYEE_DELETED:             'employee_deleted',
  EMPLOYEE_PERMISSIONS_CHANGED: 'employee_permissions_changed',

  // Reservations / schedule
  RESERVATION_CREATED:          'reservation_created',
  RESERVATION_UPDATED:          'reservation_updated',
  RESERVATION_CANCELLED:        'reservation_cancelled',
  SCHEDULE_PUBLISHED:           'schedule_published',

  // Menu management (portal side)
  MENU_ITEM_CREATED:            'menu_item_created',
  MENU_ITEM_UPDATED:            'menu_item_updated',
  MENU_ITEM_DELETED:            'menu_item_deleted',
};

// Map action → category for AuditLogView filtering
const ACTION_CATEGORY = {
  general_settings_saved:       'settings',
  pickup_settings_saved:        'settings',
  messaging_settings_saved:     'settings',
  stripe_connected:             'settings',
  courses_saved:                'settings',
  enforcement_saved:            'settings',

  employee_created:             'staff',
  employee_updated:             'staff',
  employee_deleted:             'staff',
  employee_permissions_changed: 'staff',

  reservation_created:          'reservations',
  reservation_updated:          'reservations',
  reservation_cancelled:        'reservations',
  schedule_published:           'reservations',

  menu_item_created:            'menu',
  menu_item_updated:            'menu',
  menu_item_deleted:            'menu',
};

// ── Core write function ──────────────────────────────────────────────────────

/**
 * Write a single portal audit event to Firestore.
 * Non-blocking — swallows all errors so a logging failure never blocks the
 * underlying operation.
 *
 * @param {string} restaurantId
 * @param {{ uid: string, email: string, displayName?: string }} user  — Firebase Auth user from portal session
 * @param {string} action  — one of the PORTAL_AUDIT.* constants
 * @param {object} [details]  — optional action-specific payload
 * @returns {void}  — intentionally fire-and-forget
 */
export const writePortalAuditEvent = (restaurantId, user, action, details = {}) => {
  if (!restaurantId || !user?.uid || !action) {
    console.warn('[portalAuditLog] writePortalAuditEvent called with missing args:', { restaurantId, user, action });
    return;
  }

  addDoc(
    collection(db, 'restaurants_data', restaurantId, 'audit_log'),
    {
      timestamp:    serverTimestamp(),
      employeeId:   user.uid,
      employeeName: user.displayName || user.email || 'Unknown',
      employeeRole: 'owner',
      action,
      category:     ACTION_CATEGORY[action] || 'other',
      ...details,
    }
  ).catch(err => {
    // Audit failures must never block the underlying operation.
    console.error('[portalAuditLog] write failed (non-blocking):', err);
  });
};
